'use client';

import React from 'react';

import { CreditCard, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

import Modal from '@/shared/components/Modal';
import { COPY } from '@/shared/constants/copies';
import { ExtraCreditsTier } from '@/shared/services/payment/payment.types';
import { formatUsd } from '@/shared/utils/format-money';

import { useExtraCreditsChargePreview } from '../../hooks/use-extra-credits';

type ExtraCreditsChargeConfirmModalProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tier: ExtraCreditsTier | null;
	onConfirm: () => void;
	isConfirming?: boolean;
};

/**
 * The last step before raising extra credits from Billing. The saved card is charged with no
 * Stripe page, so Pay stays disabled until the exact amount from the server is on screen.
 */
function SummaryRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-center justify-between gap-4">
			<span className="text-secondary">{label}</span>
			<span className="tabular-nums text-primary">{value}</span>
		</div>
	);
}

export const ExtraCreditsChargeConfirmModal = React.memo(function ExtraCreditsChargeConfirmModal({
	open,
	onOpenChange,
	tier,
	onConfirm,
	isConfirming,
}: ExtraCreditsChargeConfirmModalProps) {
	const { preview, isLoading, isError, isFetching, refetch } = useExtraCreditsChargePreview({
		tier,
		enabled: open,
	});

	const handleRetry = React.useCallback(() => {
		refetch();
	}, [refetch]);

	const handleCancel = React.useCallback(() => onOpenChange(false), [onOpenChange]);

	const canPay = !!preview && !isError && !isFetching;
	const isYearly = preview?.billingInterval === 'YEARLY';

	return (
		<Modal
			open={open}
			onOpenChange={onOpenChange}
			titleContent="Confirm extra credits"
			contentClassName="max-w-md"
			footerContent={
				<div className="flex w-full items-center justify-end gap-2">
					<Button
						variant="ghost"
						onClick={handleCancel}
						disabled={isConfirming}
					>
						{COPY.billing.extraCreditsCancel}
					</Button>
					<Button
						onClick={onConfirm}
						disabled={!canPay}
						disabledWithSpinner={isConfirming}
					>
						{preview ? COPY.billing.extraCreditsPay(preview.amountDue) : 'Pay'}
					</Button>
				</div>
			}
		>
			{isLoading || (isFetching && !preview) ? (
				<div className="flex justify-center py-4">
					<Loader2 className="size-5 animate-spin text-secondary" />
				</div>
			) : isError || !preview ? (
				<div className="flex items-center justify-between gap-3">
					<span className="text-sm text-secondary">
						{COPY.billing.extraCreditsChargePreviewFailed}
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={handleRetry}
						disabledWithSpinner={isFetching}
					>
						Retry
					</Button>
				</div>
			) : (
				<div className="flex flex-col gap-3 text-sm">
					<div className="flex flex-col gap-3 rounded-lg border border-border bg-muted px-4 py-3.5">
						<div className="flex flex-col gap-2">
							<SummaryRow
								label={COPY.billing.extraCreditsChargeLine}
								value={COPY.billing.extraCreditsChargePerMonth(preview.monthlyDifference)}
							/>
							{isYearly ? (
								<SummaryRow
									label={COPY.billing.extraCreditsChargeMonthsLeft}
									value={String(preview.monthsCharged)}
								/>
							) : null}
						</div>
						{/* A CSS dashed border renders as faint 1px specks; this draws real 6px dashes. */}
						<div
							aria-hidden
							className="h-px bg-[repeating-linear-gradient(to_right,currentColor_0_6px,transparent_6px_11px)] text-muted-foreground/50"
						/>
						<div className="flex items-baseline justify-between gap-4 text-primary">
							<span className="font-medium">{COPY.billing.extraCreditsChargeDueToday}</span>
							<span className="text-lg font-semibold tabular-nums">
								{formatUsd(preview.amountDue)}
							</span>
						</div>
					</div>
					<div className="flex items-start gap-1.5 text-xs text-secondary">
						<CreditCard className="mt-px size-3.5 shrink-0" />
						<span>
							{isYearly
								? COPY.billing.extraCreditsChargeYearlyNote(preview.renewalAt)
								: COPY.billing.extraCreditsChargeMonthlyNote}
						</span>
					</div>
				</div>
			)}
		</Modal>
	);
});
